import { useSelector } from 'react-redux'
import { useQueryStatistics } from 'src/queries/statistics/statistics.queries'
import { StatisticsKeys } from 'src/queries/statistics/statistics.queries.constants'
import { selectUserListModelFromState } from 'src/redux/selectors/auth.selectors'

/**
 * EX-39745 — hook replacements for the legacy orgless-user composite
 * selectors (auth.selectors) that mixed the Redux user model with counts from
 * the deleted statistics Redux slice. Statistics now come from the
 * per-stat React Query layer; the Boolean logic below is kept verbatim:
 *   - "loaded" gates map to `isSuccess` (the legacy per-stat loaded flag was
 *     set only on fetch success and never reset).
 *   - counts map to `data ?? 0` (the legacy reducer's initial count was 0).
 * Unlike the selectors, these hooks fetch their own counts on mount — React
 * Query dedup and the 30s statistics staleTime coalesce concurrent readers.
 */

interface UseOrglessUserStatusOptions {
  enabled?: boolean
}

export const useIsUserOrglessRCNOwner = ({
  enabled = true,
}: UseOrglessUserStatusOptions = {}): boolean => {
  const user = useSelector(selectUserListModelFromState)
  const { results } = useQueryStatistics({
    statisticsKeys: [StatisticsKeys.CreditCardsSourceCount] as const,
    enabled,
  })
  const sourceCount = results[StatisticsKeys.CreditCardsSourceCount]

  return (
    sourceCount.isSuccess &&
    user !== undefined &&
    !user?.isInExplicitOrg &&
    (sourceCount.data ?? 0) > 0
  )
}

export const useIsUserOrglessRCNOrBudgetOwner = ({
  enabled = true,
}: UseOrglessUserStatusOptions = {}): boolean => {
  const user = useSelector(selectUserListModelFromState)
  const { results } = useQueryStatistics({
    statisticsKeys: [
      StatisticsKeys.CreditCardsSourceCount,
      StatisticsKeys.CreditCardsDelegateOwnerMeCount,
    ] as const,
    enabled,
  })
  const sourceCount = results[StatisticsKeys.CreditCardsSourceCount]
  const delegateOwnerMeCount =
    results[StatisticsKeys.CreditCardsDelegateOwnerMeCount]

  return (
    sourceCount.isSuccess &&
    delegateOwnerMeCount.isSuccess &&
    user !== undefined &&
    !user?.isInExplicitOrg &&
    ((delegateOwnerMeCount.data ?? 0) > 0 || (sourceCount.data ?? 0) > 0)
  )
}

export const useIsUserOrglessGuestCardUser = ({
  enabled = true,
}: UseOrglessUserStatusOptions = {}): boolean => {
  const user = useSelector(selectUserListModelFromState)
  const { results } = useQueryStatistics({
    statisticsKeys: [
      StatisticsKeys.GuestVirtualCardsRecipientMeCount,
      StatisticsKeys.VirtualCardsRecipientMeCount,
    ] as const,
    enabled,
  })
  const guestCardsCount =
    results[StatisticsKeys.GuestVirtualCardsRecipientMeCount]
  const virtualCardsCount = results[StatisticsKeys.VirtualCardsRecipientMeCount]

  return (
    guestCardsCount.isSuccess &&
    virtualCardsCount.isSuccess &&
    user !== undefined &&
    !user?.isInExplicitOrg &&
    (guestCardsCount.data ?? 0) > 0 &&
    (virtualCardsCount.data ?? 0) === (guestCardsCount.data ?? 0)
  )
}

export const useIsUserOrglessRCNUser = ({
  enabled = true,
}: UseOrglessUserStatusOptions = {}): boolean => {
  const user = useSelector(selectUserListModelFromState)
  const { results } = useQueryStatistics({
    statisticsKeys: [StatisticsKeys.CreditCardsSourceCount] as const,
    enabled,
  })
  const sourceCount = results[StatisticsKeys.CreditCardsSourceCount]

  // The legacy selector had no loaded gate and no user-defined check — an
  // undefined user is treated as orgless. Kept verbatim.
  return !user?.isInExplicitOrg && (sourceCount.data ?? 0) > 0
}
